import React, { useState, useEffect } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Check, X, ClipboardCheck, MessageSquare, Clock, RefreshCw } from 'lucide-react';

interface ApprovalItem {
  id: string;
  entity_type: 'tenant' | 'branch' | 'product' | 'white_label' | 'service_provider_document' | string;
  entity_id: string;
  tenant_id: string;
  status: string;
  requested_at: string;
  metadata: any;
}

export function ApprovalCenterView() {
  const [items, setItems] = useState<ApprovalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  
  // Reject modal state
  const [rejectingItem, setRejectingItem] = useState<ApprovalItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchApprovals = async () => {
    setLoading(true);
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('v_pending_approvals')
        .select('*');
      
      if (error) {
        toast.error(error.message);
      } else {
        setItems(data || []);
      }
    } catch (err: any) {
      toast.error(err.message || 'Error fetching pending approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals();
  }, []);

  const handleResolve = async (item: ApprovalItem, decision: 'approved' | 'rejected', reason: string | null = null) => {
    setSubmittingId(item.id);
    const supabase = getSupabaseClient();
    if (!supabase) {
      setSubmittingId(null);
      return;
    }
    try {
      const { error } = await supabase.rpc('resolve_approval', {
        p_entity_type: item.entity_type,
        p_entity_id: item.entity_id,
        p_decision: decision,
        p_reason: reason || null
      });

      if (error) {
        // Show the exact error message returned by the database/backend
        toast.error(error.message);
      } else {
        toast.success(`Request successfully ${decision}`);
        // Remove item from state or refetch
        setItems(prev => prev.filter(i => i.id !== item.id));
        if (rejectingItem?.id === item.id) {
          setRejectingItem(null);
          setRejectReason('');
        }
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to resolve approval request');
    } finally {
      setSubmittingId(null);
    }
  };

  const getEntityTypeBadge = (type: string) => {
    switch (type) {
      case 'tenant':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 border-blue-200/50';
      case 'branch':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400 border-purple-200/50';
      case 'product':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border-emerald-200/50';
      case 'white_label':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border-amber-200/50';
      case 'service_provider_document':
        return 'bg-pink-100 text-pink-700 dark:bg-pink-950/40 dark:text-pink-400 border-pink-200/50';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 border-slate-200/50';
    }
  };

  const formatMetadata = (item: ApprovalItem) => {
    if (!item.metadata || typeof item.metadata !== 'object') return null;
    const entries = Object.entries(item.metadata);
    if (entries.length === 0) return null;
    
    return (
      <div className="mt-2 p-2 bg-slate-50 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-800 rounded-lg text-xs space-y-1">
        {entries.map(([key, val]) => (
          <div key={key} className="flex gap-2">
            <span className="font-bold text-slate-500 dark:text-slate-400 select-none uppercase tracking-wider text-[9px]">{key.replace(/_/g, ' ')}:</span>
            <span className="text-slate-700 dark:text-slate-300 break-all">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Title & Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            <span>Approval Center</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Review and action incoming white-label requests, service documents, custom branches, and products.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchApprovals}
          disabled={loading}
          className="flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 gap-2">
          <RefreshCw className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading pending approval queue...</p>
        </div>
      ) : items.length === 0 ? (
        <Card className="border border-dashed border-emerald-150 bg-emerald-50/10 dark:border-emerald-950 dark:bg-emerald-950/10 rounded-2xl p-12 text-center max-w-xl mx-auto">
          <CardContent className="space-y-4 pt-6">
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
              ✓
            </span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Sab kuch approved hai ✅</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
              Kuch bhi pending nahi hai. Tab tak fresh refresh kar sakte hain jab tak naya request na aaye.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="dark:bg-slate-900 dark:border-slate-800 overflow-hidden border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-800">
                <tr>
                  <th className="px-6 py-4">Requested At</th>
                  <th className="px-6 py-4">Entity Type</th>
                  <th className="px-6 py-4">Details / Metadata</th>
                  <th className="px-6 py-4">Tenant / Entity ID</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/40 dark:hover:bg-slate-950/20 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-slate-400">
                      <div className="flex items-center gap-1.5 text-xs">
                        <Clock className="w-3.5 h-3.5 text-slate-400" />
                        <span>{new Date(item.requested_at).toLocaleString()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2.5 py-0.5 border rounded-full text-[10px] font-extrabold uppercase tracking-wider ${getEntityTypeBadge(item.entity_type)}`}>
                        {item.entity_type.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 max-w-md">
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-semibold mb-1">
                        ID: <span className="font-mono text-slate-800 dark:text-slate-200">{item.entity_id}</span>
                      </div>
                      {formatMetadata(item)}
                    </td>
                    <td className="px-6 py-4 text-xs font-mono text-slate-500 dark:text-slate-400">
                      Tenant: {item.tenant_id || 'Global'}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          disabled={submittingId !== null}
                          onClick={() => handleResolve(item, 'approved')}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs font-bold"
                        >
                          <Check className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={submittingId !== null}
                          onClick={() => setRejectingItem(item)}
                          className="h-8 text-xs font-bold"
                        >
                          <X className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Reject Reason Modal */}
      {rejectingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200 dark:border-slate-800">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-950/50">
              <h3 className="text-md font-bold text-slate-950 dark:text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-red-600" />
                <span>Specify Rejection Reason</span>
              </h3>
              <button 
                onClick={() => { setRejectingItem(null); setRejectReason(''); }} 
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                You are rejecting the request for entity type <strong className="uppercase">{rejectingItem.entity_type}</strong>. 
                Enter the reason below (optional) so the merchant can see it.
              </p>
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Rejection Reason</label>
                <Input
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g. Document copy is blurry or incomplete"
                  className="dark:bg-slate-950"
                  autoFocus
                />
              </div>
            </div>
            <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 flex justify-end gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setRejectingItem(null); setRejectReason(''); }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleResolve(rejectingItem, 'rejected', rejectReason)}
                disabled={submittingId !== null}
              >
                Confirm Reject
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
